import React, { useEffect, useState } from "react";
import axios from "axios";
import { Calendar, MapPin } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso, lang) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

export const Concerti = () => {
  const { t, lang } = useLanguage();
  const [concerts, setConcerts] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/concerts`)
      .then((res) => setConcerts(res.data || []))
      .catch((err) => console.error("Failed to load concerts", err));
  }, []);

  const upcoming = concerts.filter((c) => c.upcoming);
  const past = concerts.filter((c) => !c.upcoming);

  const renderConcert = (c, idx, isPast = false) => (
    <article
      key={c.id}
      data-testid={`concert-${c.id}`}
      className={`grid md:grid-cols-12 gap-6 py-8 border-b border-white/8 group ${
        isPast ? "opacity-80 hover:opacity-100" : ""
      } transition-opacity`}
    >
      <div className="md:col-span-2">
        <div className="font-serif-bodoni text-[#D4AF37] text-2xl lg:text-3xl tracking-tight">
          {formatDate(c.date, lang)}
        </div>
        {c.time && (
          <div className="text-xs tracking-[0.2em] uppercase text-[#71717A] mt-1">
            h. {c.time}
          </div>
        )}
      </div>
      <div className="md:col-span-7">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#71717A] mb-2">
          {c.ensemble}
        </div>
        <h3 className="font-serif-display text-2xl lg:text-3xl text-[#FDFBF7] leading-tight group-hover:text-[#D4AF37] transition-colors">
          {c.title}
        </h3>
        {c.program && (
          <p className="mt-3 text-[#A1A1AA] text-sm leading-relaxed font-light italic">
            {c.program}
          </p>
        )}
        {c.performers && c.performers.length > 0 && (
          <ul className="mt-4 space-y-1">
            {c.performers.map((p, i) => (
              <li
                key={i}
                className="text-[13px] text-[#A1A1AA] font-light leading-snug"
              >
                {p}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="md:col-span-3 md:text-right">
        <div className="inline-flex md:flex-col md:items-end items-center gap-2 text-[#D1D1D1]">
          <MapPin size={14} className="text-[#D4AF37]" />
          <div className="text-sm font-light">{c.venue}</div>
        </div>
        <div className="text-xs text-[#71717A] mt-1">{c.city}</div>
      </div>
    </article>
  );

  return (
    <section
      id="concerts"
      data-testid="concerts-section"
      className="relative py-28 lg:py-36 bg-[#0A0A0A] border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between gap-6 mb-16 flex-wrap">
          <div className="max-w-2xl">
            <div className="overline" data-testid="concerts-overline">
              {t.concerts.overline}
            </div>
            <h2
              data-testid="concerts-title"
              className="font-serif-display font-light mt-5 text-4xl md:text-5xl lg:text-6xl text-[#FDFBF7] leading-[1.05]"
            >
              {t.concerts.title}
            </h2>
            <p className="mt-6 text-[#A1A1AA] text-base lg:text-lg font-light leading-relaxed">
              {t.concerts.subtitle}
            </p>
          </div>
          <Calendar className="text-[#D4AF37] hidden md:block" size={48} strokeWidth={1} />
        </div>

        {upcoming.length > 0 && (
          <div data-testid="upcoming-concerts">
            <div className="flex items-center gap-4 mb-2">
              <span className="w-2 h-2 bg-[#D4AF37] rounded-full animate-pulse" />
              <h3 className="overline">{t.concerts.upcoming}</h3>
            </div>
            <div className="border-t border-white/15">
              {upcoming.map((c, i) => renderConcert(c, i, false))}
            </div>
          </div>
        )}

        {upcoming.length === 0 && (
          <div
            className="text-[#71717A] italic font-serif-display text-xl py-8"
            data-testid="no-upcoming"
          >
            {t.concerts.empty}
          </div>
        )}

        {past.length > 0 && (
          <div className="mt-20" data-testid="past-concerts">
            <h3 className="overline mb-2">{t.concerts.past}</h3>
            <div className="border-t border-white/10">
              {past.map((c, i) => renderConcert(c, i, true))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Concerti;
